import { LabelSchema } from './chunk-DEPQ2OSO.js';
import { z } from 'zod';

var CHART_MAX_POINTS = 50;
var CHART_MAX_SERIES = 12;
var ChartKindSchema = /* @__PURE__ */ z.enum(["bar", "line", "area", "pie", "donut", "scatter", "radar"]);
var ChartValueFormatSchema = /* @__PURE__ */ z.enum(["number", "currency", "percent", "compact"]);
var CHART_INTERPOLATIONS = ["smooth", "linear", "stepped"];
var CurrencyCodeSchema = /* @__PURE__ */ z.string().regex(/^[A-Z]{3}$/);
var ChartColorSchema = /* @__PURE__ */ z.string().regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/);
var ChartDataPointSchema = /* @__PURE__ */ z.object({
  label: LabelSchema.optional(),
  value: z.number().finite(),
  x: z.number().finite().optional()
});
var ChartSeriesSchema = /* @__PURE__ */ z.object({
  name: LabelSchema,
  color: ChartColorSchema.optional(),
  data: z.array(ChartDataPointSchema).min(1).max(CHART_MAX_POINTS)
});
var BAND_TONES = ["success", "warning", "danger", "neutral"];
var DataSourceRefSchema = /* @__PURE__ */ z.object({
  /** The id the host registered the data source under. */
  id: z.string().min(1).max(80),
  /** Opaque parameters forwarded to the host fetcher (e.g. a filter, a range). */
  params: z.record(z.string(), z.unknown()).optional()
});
var MAX_DATA_SOURCES = 24;
var DataSourceCatalogParamSchema = /* @__PURE__ */ z.object({
  name: z.string().min(1).max(80),
  description: z.string().max(280).optional()
});
var DataSourceCatalogEntrySchema = /* @__PURE__ */ z.object({
  id: z.string().min(1).max(80),
  description: z.string().max(280).optional(),
  params: z.array(DataSourceCatalogParamSchema).max(24).optional()
});
var DataSourceCatalogSchema = /* @__PURE__ */ z.array(DataSourceCatalogEntrySchema).max(MAX_DATA_SOURCES);

// src/compositions/welcome-open.ts
var WELCOME_MODES = ["off", "greeting-only", "digest"];
var DISMISSAL_LIMIT = 2;
function decideWelcomeOpen(mode, signals) {
  const refusals = [];
  if (mode === "off") refusals.push("mode-off");
  if (mode === "greeting-only") refusals.push("mode-greeting-only");
  if (signals.priorConversations <= 0) refusals.push("first-visit");
  if (signals.dismissals >= DISMISSAL_LIMIT) refusals.push("dismissed-enough");
  if (signals.deltas <= 0) refusals.push("no-deltas");
  return { open: refusals.length === 0, refusals };
}
var WELCOME_DIGEST_SCALES = ["single", "set"];
var CompositionExperienceSchema = /* @__PURE__ */ z.object({
  /** Off / greeting only / digest. Narrowing only — see `decideWelcomeOpen`. */
  welcomeMode: z.enum(WELCOME_MODES),
  /**
   * How large the digest may be when one is offered at all.
   *
   * Kept even while `welcomeMode` is not `digest`, rather than cleared: an
   * operator who turns the digest off for a week and back on has not asked to
   * lose the size they chose, and re-deriving a default on the way back would
   * silently change what their visitors see.
   */
  welcomeDigestScale: z.enum(WELCOME_DIGEST_SCALES)
}).strict();
var DEFAULT_COMPOSITION_EXPERIENCE = {
  welcomeMode: "greeting-only",
  welcomeDigestScale: "single"
};

// src/compositions/tool-name.ts
var PRESENT_COMPOSITION_TOOL_NAME = "present_composition";
var REMEMBER_PREFERENCE_TOOL_NAME = "remember_presentation_preference";
var FORGET_PREFERENCE_TOOL_NAME = "forget_presentation_preference";

// src/compositions/describe-structure.ts
var DESCRIBED_BLOCK_CAP = 12;
var TITLE_CAP = 120;
function readString(value, cap) {
  if (typeof value !== "string") return void 0;
  const trimmed = value.trim();
  if (trimmed === "") return void 0;
  return trimmed.length > cap ? trimmed.slice(0, cap) : trimmed;
}
function describeCompositionStructure(value) {
  const envelope = value;
  const raw = envelope !== null && typeof envelope === "object" && envelope.spec !== void 0 ? envelope.spec : value;
  if (raw === null || typeof raw !== "object") {
    return { regionCount: 0, elementCount: 0, blocks: [] };
  }
  const spec = raw;
  const regionCount = Array.isArray(spec.regions) ? spec.regions.length : 0;
  const elements = spec.elements !== null && typeof spec.elements === "object" && !Array.isArray(spec.elements) ? Object.values(spec.elements) : [];
  const blocks = [];
  for (const element of elements) {
    if (blocks.length >= DESCRIBED_BLOCK_CAP) break;
    const block = readString(element?.block, 80);
    if (block !== void 0) blocks.push(block);
  }
  const title = readString(spec.title, TITLE_CAP);
  const scale = readString(spec.scale, 16);
  return {
    ...title !== void 0 ? { title } : {},
    ...scale !== void 0 ? { scale } : {},
    regionCount,
    elementCount: elements.length,
    blocks
  };
}
function compositionTranscriptContent(structure, blocksLabel) {
  const lines = [];
  if (structure.title !== void 0 && structure.title !== "") lines.push(structure.title);
  if (structure.blocks.length > 0) {
    lines.push(`${blocksLabel}: ${structure.blocks.join(", ")}`);
  }
  return lines.join("\n");
}

export { BAND_TONES, CHART_INTERPOLATIONS, CHART_MAX_POINTS, CHART_MAX_SERIES, ChartColorSchema, ChartDataPointSchema, ChartKindSchema, ChartSeriesSchema, ChartValueFormatSchema, CompositionExperienceSchema, CurrencyCodeSchema, DEFAULT_COMPOSITION_EXPERIENCE, DESCRIBED_BLOCK_CAP, DISMISSAL_LIMIT, DataSourceCatalogEntrySchema, DataSourceCatalogParamSchema, DataSourceCatalogSchema, DataSourceRefSchema, FORGET_PREFERENCE_TOOL_NAME, MAX_DATA_SOURCES, PRESENT_COMPOSITION_TOOL_NAME, REMEMBER_PREFERENCE_TOOL_NAME, WELCOME_DIGEST_SCALES, WELCOME_MODES, compositionTranscriptContent, decideWelcomeOpen, describeCompositionStructure };
//# sourceMappingURL=chunk-FOR36YEU.js.map
//# sourceMappingURL=chunk-FOR36YEU.js.map